// One evaluation, in full: its recordings (up to three), the five criterion scores, any
// fatal mistakes, the QMS note, and the objection thread.
//
// The same screen for every role — what differs is the ACTION in the sidebar:
//   the evaluated agent gets "İtiraz et" (only on a non-perfect score, still inside the
//     one-week window — the server's `canObject` decides, not this screen)
//   supervisor/admin get the resolve form once an objection is open (server canResolve)
//   an EDIT form for whoever may re-score THIS row (server `canEdit`): qms before the one
//     objection, supervisor/admin after — supervisors and post-objection edits must state a
//     reason (`editReasonRequired`).
//   both forms take an optional ADDED note (owner 2026-09-20): the editor's or resolver's
//     own words to the agent, stored under THEIR name (`addedNotes`, migration 011) — the
//     QMS specialist's note is never rewritten in their name by someone else.
// Every one of these flags comes from the server so the button and the rule cannot drift.

import { useMemo, useState } from 'react';
import * as api from '../api';
import type { Meta, PanelUser } from '../types';
import { useAsync } from '../hooks';
import {
  Avatar, BreakdownRow, Callout, CodeTile, CriticalBanner, Empty, Icon, Loading, Note, PageHead, Panel, Pill,
  ScoreHero,
} from '../ui';
import {
  ROLE_KICKER, STATUS_LABELS, STATUS_TONE, fileSize, fullDateTime, score10, scoreTone, toLocalInput, toneVar,
} from '../format';
import { audioRequired, liveScore, noteRequired } from '../livescore';
import { navigate } from '../App';
import ScoreForm from './ScoreForm';

export default function EvalDetail({ id, user, meta, onChanged }: {
  id: number; user: PanelUser; meta: Meta | null; onChanged: () => void;
}) {
  const state = useAsync(() => api.getEvaluation(id), [id]);
  const [objectionText, setObjectionText] = useState('');
  const [reply, setReply] = useState('');
  const [revising, setRevising] = useState(false);
  // Editing is a SEPARATE mode from revising: revising answers an objection (and needs a
  // reply), editing is a plain correction. They share the score state because the form is
  // the same, but never run at once — startEdit/startRevision each clear the other.
  const [editing, setEditing] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  // The call's date/time, as a datetime-local value. Seeded from the row on startEdit; an
  // unchanged value is simply re-sent and lands on the same instant.
  const [editCallAt, setEditCallAt] = useState('');
  // The called member's user id. Seeded from the row; empty is re-sent as "keep".
  const [editPlayerUserId, setEditPlayerUserId] = useState('');
  const [editReason, setEditReason] = useState('');
  // A note ADDED under the viewer's own name. Shared by the edit form and the resolve form
  // (they never show at once); cleared whenever either form opens or closes.
  const [addNote, setAddNote] = useState('');
  // An edit ADDS recordings and removes them by id — it never replaces silently. A row may
  // end up holding meta.audio.maxFiles of them (owner 2026-09-10).
  const [editAudios, setEditAudios] = useState<File[]>([]);
  const [editRemoveIds, setEditRemoveIds] = useState<number[]>([]);
  const [editAudioError, setEditAudioError] = useState('');
  const [editFileKey, setEditFileKey] = useState(0);
  const [scores, setScores] = useState<Record<string, number | null>>({});
  const [criticals, setCriticals] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  // Deleting is irreversible and takes the recordings with it, so the button arms first and
  // only the second click sends — the app has no modal, and a window.confirm would sit
  // outside the panel's own language (i18n translates the DOM, not native dialogs).
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  const criteria = meta?.criteria ?? [];
  const rules = meta?.criticalRules ?? [];
  const audioExts = meta?.audio?.extensions ?? ['.mp3', '.wav', '.m4a', '.ogg'];
  const audioRequiredFlag = meta?.audio?.required ?? true;
  const audioMaxFiles = meta?.audio?.maxFiles ?? 3;
  const noteThreshold = meta?.notes?.requiredBelow ?? 10;
  const live = useMemo(() => liveScore(scores, criticals, criteria), [scores, criticals, criteria]);

  // An id that does not exist (or is not visible) answers 404, and an unparseable one 400.
  // Both used to render a bare error line with no heading and nothing to navigate back
  // from — a page that looks broken rather than empty.
  if (state.error) {
    return (
      <>
        <PageHead icon="fact_check" title="Çağrı Değerlendirmesi" subtitle="Bu kayıt görüntülenemiyor." />
        <Note error>{state.error}</Note>
        <div className="filters">
          <button type="button" className="btn sm" onClick={() => navigate('evaluations')}>
            <Icon name="arrow_back" /> Değerlendirmelere dön
          </button>
        </div>
      </>
    );
  }
  if (!state.data) return <Loading />;
  const e = state.data;

  // From the server (engine/access.js canResolveObjections + this row's open objection):
  // supervisor/admin only since 2026-09-18. A qms now READS the thread (owner 2026-09-22) but
  // still never answers it, so `canResolve` is exactly what keeps the reply form shut for it —
  // deriving it from "the thread is on screen" would open a form the resolve route refuses.
  const canResolve = e.canResolve;
  // The edit form runs the SAME rules the create form does: the server re-checks both against
  // its own number and refuses a mismatch (Rules 6 and 7). The note tracks the live preview;
  // audio is now required on every call (owner 2026-09-07) but is satisfied by the recordings
  // the row ALREADY has — an edit only has to attach one when it would otherwise end up with
  // none (a legacy row, or every stored recording marked for removal).
  const editNeedsNote = noteRequired(live, noteThreshold);
  const editNeedsAudio = audioRequired(audioRequiredFlag);
  // A reason is mandatory when the server says so — a supervisor edit, or a correction to a
  // row whose objection was already answered (detail.editReasonRequired). The server re-checks.
  const editNeedsReason = e.editReasonRequired;
  // What the row would hold after this edit — the same arithmetic the server does under the
  // row lock (engine/audio-set.js planAudioSet). The button is only drawn from it; the gate
  // is the server's.
  const editKeptAudios = e.audios.filter((a) => !editRemoveIds.includes(a.id));
  const editAudioCount = editKeptAudios.length + editAudios.length;
  const editAudioRoom = audioMaxFiles - editAudioCount;
  const canSaveEdit = live.complete
    && Boolean(editCallAt)
    && (!editNeedsNote || Boolean(editNotes.trim()))
    && (!editNeedsAudio || editAudioCount > 0)
    && editAudioCount <= audioMaxFiles
    && (!editNeedsReason || Boolean(editReason.trim()));
  const finalColor = toneVar(scoreTone(e.finalScore, e.hasCritical));

  /** Seed the revision form from the CURRENT scores so a QMS specialist edits, not retypes. */
  function startRevision() {
    setScores(Object.fromEntries(e.scores.map((s) => [s.key, s.score])));
    setCriticals(e.criticals.map((c) => c.key));
    setEditing(false);
    setRevising(true);
  }

  /** Same seeding for a plain edit, plus the note — which is what usually needs fixing. */
  function startEdit() {
    setScores(Object.fromEntries(e.scores.map((s) => [s.key, s.score])));
    setCriticals(e.criticals.map((c) => c.key));
    setEditNotes(e.notes || '');
    setEditCallAt(toLocalInput(e.callAt));
    setEditPlayerUserId(e.playerUserId == null ? '' : String(e.playerUserId));
    setEditReason('');
    setAddNote('');
    resetEditAudio();
    setRevising(false);
    setEditing(true);
    setError('');
  }

  function resetEditAudio() {
    setEditAudios([]);
    setEditRemoveIds([]);
    setEditAudioError('');
    setEditFileKey((k) => k + 1);
  }

  /**
   * Add the picked files to the pending set. An over-cap pick is REFUSED whole rather than
   * trimmed, exactly as the server refuses it (engine/audio-set.js): keeping the first N of
   * what someone attached stores evidence nobody chose.
   */
  function addEditAudios(list: FileList | null) {
    const picked = Array.from(list ?? []);
    setEditFileKey((k) => k + 1);
    if (!picked.length) return;
    if (picked.length > editAudioRoom) {
      setEditAudioError(`En fazla ${audioMaxFiles} çağrı kaydı eklenebilir.`);
      return;
    }
    setEditAudioError('');
    setEditAudios((a) => [...a, ...picked]);
  }

  /** Mark a STORED recording for deletion. It is only gone once the edit is saved. */
  function toggleRemoveAudio(audioId: number) {
    setEditAudioError('');
    setEditRemoveIds((ids) => (ids.includes(audioId) ? ids.filter((x) => x !== audioId) : [...ids, audioId]));
  }

  function cancelEdit() {
    setEditing(false);
    setEditCallAt('');
    setAddNote('');
    resetEditAudio();
    setError('');
  }

  async function submitEdit() {
    if (busy || !live.complete) return;
    setBusy(true); setError('');
    try {
      await api.editEvaluation(e.id, {
        scores: Object.fromEntries(criteria.map((c) => [c.key, scores[c.key] as number])),
        criticals,
        notes: editNotes.trim(),
        reason: editReason.trim(),
        note: addNote.trim(),
        callAt: editCallAt,
        playerUserId: editPlayerUserId.trim(),
        audios: editAudios,
        removeAudioIds: editRemoveIds,
      });
      setEditing(false);
      setAddNote('');
      resetEditAudio();
      state.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : 'Değerlendirme güncellenemedi.');
    } finally { setBusy(false); }
  }

  /**
   * Delete the evaluation outright (owner 2026-09-09: a QMS specialist may remove one for the
   * same 12h they may edit it). The server decides — `e.canDelete` only draws the button — and
   * the row, its scores, its criticals and its recording all go with it, so there is nothing
   * to come back to: the list is where we land afterwards.
   */
  async function submitDelete() {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await api.deleteEvaluation(e.id);
      onChanged();
      navigate('evaluations');
    } catch (err) {
      setConfirmDelete(false);
      setError(err instanceof api.ApiError ? err.message : 'Değerlendirme silinemedi.');
    } finally { setBusy(false); }
  }

  async function submitObjection() {
    if (!objectionText.trim() || busy) return;
    setBusy(true); setError('');
    try {
      await api.createObjection(e.id, objectionText.trim());
      setObjectionText('');
      state.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : 'İtiraz gönderilemedi.');
    } finally { setBusy(false); }
  }

  async function submitResolution(resolution: 'revised' | 'upheld') {
    if (busy || !e.objection) return;
    setBusy(true); setError('');
    try {
      await api.resolveObjection(e.objection.id, {
        resolution,
        reply: reply.trim(),
        ...(addNote.trim() ? { note: addNote.trim() } : {}),
        ...(resolution === 'revised'
          ? {
              scores: Object.fromEntries(criteria.map((c) => [c.key, scores[c.key] as number])),
              criticals,
            }
          : {}),
      });
      setReply(''); setAddNote(''); setRevising(false);
      state.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : 'İtiraz sonuçlandırılamadı.');
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHead
        icon="fact_check"
        title="Çağrı Değerlendirmesi"
        subtitle={`#${e.id}${e.callRef ? ` · ${e.callRef}` : ''} · ${e.agentName}${e.listName ? ` · ${e.listName}` : ''}`}
        right={<Pill tone={STATUS_TONE[e.status]} lg>{STATUS_LABELS[e.status]}</Pill>}
      />

      <div className="review">
        <div className="review-main">
          <section className="panel">
            <div className="callhead">
              <Avatar name={e.agentName} size="xl" kind={e.hasCritical ? 'bad' : 'accent'} />
              <div style={{ minWidth: 0 }}>
                <div className="nm">{e.agentName}</div>
                <div className="sub">
                  Agent · {e.brandKey}{e.listName ? ` · ${e.listName}` : ''}{e.callType && e.callType !== e.listName ? ` · ${e.callType}` : ''}
                </div>
              </div>
              <div className="callmeta">
                <div><div className="micro">Tarih</div><div className="v">{fullDateTime(e.callAt)}</div></div>
                <div><div className="micro">Kullanıcı ID</div><div className="v" translate="no">{e.playerUserId ?? '—'}</div></div>
                <div><div className="micro">Değerlendiren</div><div className="v">{e.evaluatorName}</div></div>
              </div>
            </div>
            {/* One call may have been recorded in several files — a transfer, a call-back, a
                recorder rollover — so ALL of them are stacked here rather than one being
                picked for the reader. A row scored under the old `final <= 7` rule can still
                carry none: say so, because an <audio> element pointed at a 404 renders as a
                broken, silent player. */}
            {e.audios.length ? e.audios.map((a, i) => (
              <div className="player" key={a.id}>
                {/* The browser fetches this with the session cookie; the API checks the role
                    and answers Range requests so seeking works without the whole file. */}
                <Icon name="mic" style={{ color: 'var(--accent)' }} />
                <audio controls preload="metadata" src={api.audioSrc(a.url)}>
                  Tarayıcınız ses oynatmayı desteklemiyor.
                </audio>
                <span className="meta">
                  {e.audios.length > 1 ? `${i + 1}/${e.audios.length} · ` : ''}
                  {a.name || 'çağrı kaydı'} · {fileSize(a.bytes)}
                </span>
              </div>
            )) : (
              <div className="player">
                <Icon name="mic_off" style={{ color: 'var(--faint)' }} />
                <span className="meta">Bu çağrı için kayıt yüklenmedi.</span>
              </div>
            )}
          </section>

          <section className="panel">
            <div className="sec-hd">
              <CodeTile>%100</CodeTile>
              <div style={{ minWidth: 0 }}>
                <div className="nm">Kriter puanları</div>
                <div className="sub">Her kriter ağırlığına göre toplam puana katılır.</div>
              </div>
              <span className="sec-score" style={{ color: finalColor }}>{e.hasCritical ? '0,0' : score10(e.finalScore)}</span>
            </div>
            <div className="sec-body">
              {e.scores.map((s) => (
                <div className="item" key={s.key}>
                  <div className="item-txt">
                    <span>{s.label}</span>
                    <span className="w" translate="no">%{s.weight}</span>
                    {s.hint ? <div className="item-hint">{s.hint}</div> : null}
                  </div>
                  <span className="item-val" style={{ color: toneVar(scoreTone(s.score)) }}>{score10(s.score)}</span>
                </div>
              ))}
            </div>
          </section>

          {e.criticals.length > 0 ? <CriticalBanner names={e.criticals.map((c) => c.label)} /> : null}

          {revising || editing ? (
            <>
              <Callout tone="warn" icon="edit_note">
                <b>Yeni puanlama</b><span> · </span><span>Mevcut puanlar forma yüklendi; değiştirip sağdan kaydedin.</span>
              </Callout>
              <ScoreForm
                criteria={criteria} rules={rules} scores={scores} criticals={criticals}
                onScore={(key, v) => setScores((s) => ({ ...s, [key]: v }))}
                onCritical={(key, on) => setCriticals((c) => (on ? [...c, key] : c.filter((x) => x !== key)))}
              />
            </>
          ) : null}

          {e.notes ? (
            <Panel icon="edit_note" title="QMS notu ve agent'a geri bildirim" hint={`${e.evaluatorName} · ${fullDateTime(e.createdAt)}`}>
              <div className="panel-body">
                <div style={{ display: 'flex', gap: 12 }}>
                  <Avatar name={e.evaluatorName} size="lg" />
                  <div style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--dim)', whiteSpace: 'pre-wrap', minWidth: 0 }}>
                    {e.notes}
                  </div>
                </div>
              </div>
            </Panel>
          ) : null}

          {/* ── added notes (migration 011) ──────────────────────────────── */}
          {e.addedNotes.length > 0 ? (
            <Panel icon="speaker_notes" title="Eklenen notlar" hint="Düzenleme ve itiraz yanıtı sırasında bırakılan notlar, yazarının adıyla.">
              <div className="panel-body tight">
                {e.addedNotes.map((n) => (
                  <div className="msg" key={n.id}>
                    <div className="msg-hd">
                      <Avatar name={n.authorName} size="sm" />
                      <span className="who">{n.authorName}</span>
                      <span className="faint" style={{ fontSize: 11 }}>{ROLE_KICKER[n.authorRole] || n.authorRole}</span>
                      <span className="when">{fullDateTime(n.createdAt)}</span>
                    </div>
                    <div className="msg-txt" style={{ whiteSpace: 'pre-wrap' }}>{n.body}</div>
                  </div>
                ))}
              </div>
            </Panel>
          ) : null}

          {/* ── objection thread ─────────────────────────────────────────── */}
          {e.objection ? (
            <Panel
              icon="forum"
              title="İtiraz"
              hint={e.objection.resolvedAt ? `${e.objection.resolverName} · ${fullDateTime(e.objection.resolvedAt)}` : 'Yanıt bekleniyor'}
              right={e.objection.resolvedAt
                ? <Pill tone={e.objection.resolution === 'revised' ? 'good' : 'neu'}>{e.objection.resolution === 'revised' ? 'Puan güncellendi' : 'İtiraz reddedildi'}</Pill>
                : <Pill tone="warn">Bekliyor</Pill>}
            >
              <div className="panel-body tight">
                <div className="msg">
                  <div className="msg-hd">
                    <Avatar name={e.objection.agentName} size="sm" kind="neutral" />
                    <span className="who">{e.objection.agentName}</span>
                    <span className="when">{fullDateTime(e.objection.createdAt)}</span>
                  </div>
                  <div className="msg-txt">{e.objection.body}</div>
                </div>
                {e.objection.resolvedAt ? (
                  <div className="msg">
                    <div className="msg-hd">
                      <Avatar name={e.objection.resolverName || 'QMS'} size="sm" />
                      <span className="who">{e.objection.resolverName}</span>
                      <span className="when">{fullDateTime(e.objection.resolvedAt)}</span>
                    </div>
                    <div className="msg-txt">
                      <b style={{ color: 'var(--text)' }}>
                        {e.objection.resolution === 'revised' ? 'Puan güncellendi' : 'İtiraz reddedildi'}
                      </b>
                      {e.objection.reply ? <><br />{e.objection.reply}</> : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </Panel>
          ) : null}

          {e.revisions.length > 0 ? (
            <Panel icon="history" title="Puan geçmişi" hint="Her güncelleme öncesi puan kalıcı olarak saklanır.">
              <div className="critlist">
                {e.revisions.map((r, i) => {
                  // What this snapshot was replaced BY: the next revision's snapshot, or —
                  // for the newest one — the row as it stands now. Only then can the line say
                  // a date MOVED; comparing every entry against the current row would mark
                  // every older revision as a date change too.
                  const nextCallAt = i + 1 < e.revisions.length ? e.revisions[i + 1].prevCallAt : e.callAt;
                  const movedDate = r.prevCallAt && nextCallAt && r.prevCallAt !== nextCallAt
                    ? `${fullDateTime(r.prevCallAt)} → ${fullDateTime(nextCallAt)}`
                    : '';
                  return (
                    <div className="critline" key={r.id}>
                      <span className="tag">{score10(r.prevFinal)}</span>
                      <span className="grow">
                        {fullDateTime(r.createdAt)} · {r.revisedByName} tarafından güncellendi
                        {movedDate ? <><br /><span className="dim">Çağrı tarihi: {movedDate}</span></> : null}
                        {r.reason ? <><br /><span className="dim">{r.reason}</span></> : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Panel>
          ) : null}
        </div>

        {/* ── sidebar ─────────────────────────────────────────────────────── */}
        <div className="review-side">
          <div className="sticky">
            <section className="panel">
              {revising || editing ? (
                <ScoreHero score={live.final} hasCritical={live.hasCritical} label="Yeni toplam" />
              ) : (
                <ScoreHero score={e.finalScore} hasCritical={e.hasCritical} label={e.qualityLabel} />
              )}
              <div className="brk">
                {revising || editing
                  ? criteria.map((c) => (
                      <BreakdownRow key={c.key} label={c.label} weight={c.weight} value={scores[c.key] ?? null} />
                    ))
                  : e.scores.map((s) => (
                      <BreakdownRow key={s.key} label={s.label} weight={s.weight} value={s.score} />
                    ))}
                {((revising || editing) ? live.hasCritical : e.hasCritical) ? (
                  <div className="brk-row kv">
                    <span className="nm">Kritik hata</span>
                    <span className="val" style={{ color: 'var(--bad)' }}>{(revising || editing) ? criticals.length : e.criticals.length}</span>
                  </div>
                ) : null}
              </div>

              {e.canObject ? (
                <div className="panel-foot">
                  <div className="field">
                    <label htmlFor="ob">İtirazın</label>
                    <textarea
                      id="ob" className="inp" style={{ minHeight: 88 }}
                      value={objectionText} onChange={(ev) => setObjectionText(ev.target.value)}
                      placeholder="Hangi kritere neden itiraz ettiğini yaz…"
                    />
                  </div>
                  {error ? <Note error>{error}</Note> : null}
                  <button className="btn pri full" type="button"
                    disabled={busy || !objectionText.trim()} onClick={submitObjection}>
                    <Icon name="gavel" /><span>{busy ? 'Gönderiliyor…' : 'İtiraz et'}</span>
                  </button>
                  <Callout center>
                    İtirazın QMS uzmanına iletilir. Her değerlendirmeye bir kez itiraz edilebilir.
                  </Callout>
                </div>
              ) : null}

              {editing ? (
                <div className="panel-foot">
                  {/* The call's own date. Scoring is retrospective, so this is the field that
                      gets mistyped most — and it decides which report week the call counts in,
                      which is why it is editable here and audited in the revision history. */}
                  <div className="field">
                    <label htmlFor="ec">Çağrı tarihi ve saati * <span>(İstanbul)</span></label>
                    <input
                      id="ec" className="inp" type="datetime-local"
                      value={editCallAt} onChange={(ev) => setEditCallAt(ev.target.value)}
                    />
                    <span className="hint">Değiştirirseniz çağrı, dönem raporlarında yeni tarihine taşınır.</span>
                  </div>
                  <div className="field">
                    <label htmlFor="euid">Kullanıcı ID</label>
                    <input
                      id="euid" className="inp" inputMode="numeric" pattern="[0-9]*"
                      value={editPlayerUserId}
                      onChange={(ev) => setEditPlayerUserId(ev.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="123456"
                    />
                    <span className="hint">Aranan üyenin BackOffice kullanıcı numarası.</span>
                  </div>
                  <div className="field">
                    <label htmlFor="en">{editNeedsNote ? 'QMS notu *' : 'QMS notu'}</label>
                    <textarea
                      id="en" className="inp" style={{ minHeight: 88 }}
                      value={editNotes} onChange={(ev) => setEditNotes(ev.target.value)}
                      placeholder="Agent'a iletilecek gözlemler…"
                    />
                  </div>
                  {/* The editor's OWN note, appended under their name — the field above is the
                      specialist's text. Optional; never required by the score. */}
                  <div className="field">
                    <label htmlFor="ean">Not ekle</label>
                    <textarea
                      id="ean" className="inp" style={{ minHeight: 72 }}
                      value={addNote} onChange={(ev) => setAddNote(ev.target.value)}
                      placeholder="Kendi adınızla eklenecek not…"
                    />
                    <span className="hint">QMS notunun altında, adınız ve saatiyle agent'a gösterilir.</span>
                  </div>
                  {/* The recordings. A file attached here is ADDED (a call split across a
                      transfer or a call-back keeps both halves); removing one is asked for
                      explicitly, so fixing a note can never quietly drop the evidence. The
                      removal only happens when the edit is saved. */}
                  <div className="field">
                    <label htmlFor="ea">
                      {editNeedsAudio && editAudioCount === 0 ? 'Çağrı kayıtları *' : 'Çağrı kayıtları'}
                    </label>
                    {e.audios.map((a) => {
                      const dropped = editRemoveIds.includes(a.id);
                      return (
                        <div className="filerow" key={a.id}>
                          <Icon
                            name={dropped ? 'mic_off' : 'graphic_eq'}
                            style={{ color: dropped ? 'var(--bad)' : 'var(--accent)', fontSize: 18 }}
                          />
                          <span className="nm" style={dropped ? { textDecoration: 'line-through' } : undefined}>
                            {a.name || 'çağrı kaydı'}
                          </span>
                          <span className="sz">{fileSize(a.bytes)}</span>
                          <button
                            className="btn ghost sm" type="button"
                            title={dropped ? 'Silmekten vazgeç' : 'Kaydı sil'}
                            onClick={() => toggleRemoveAudio(a.id)}
                          >
                            <Icon name={dropped ? 'history' : 'delete'} />
                          </button>
                        </div>
                      );
                    })}
                    {editAudios.map((f, i) => (
                      <div className="filerow" key={`new-${f.name}-${f.size}-${i}`}>
                        <Icon name="upload_file" style={{ color: 'var(--good)', fontSize: 18 }} />
                        <span className="nm">{f.name}</span>
                        <span className="sz">{fileSize(f.size)}</span>
                        <button
                          className="btn ghost sm" type="button" title="Kaydı çıkar"
                          onClick={() => setEditAudios((a) => a.filter((_, x) => x !== i))}
                        >
                          <Icon name="close" />
                        </button>
                      </div>
                    ))}
                    {editAudioRoom > 0 ? (
                      <input
                        key={editFileKey}
                        id="ea" className="inp" type="file" multiple
                        accept={`${audioExts.join(',')},audio/*`}
                        onChange={(ev) => addEditAudios(ev.target.files)}
                      />
                    ) : null}
                    {editAudioError
                      ? <span className="hint" style={{ color: 'var(--bad)' }}>{editAudioError}</span>
                      : <span className="hint">{`En fazla ${audioMaxFiles} kayıt · yeni dosya eklenir, mevcut kayıt silinmez.`}</span>}
                  </div>
                  <div className="field">
                    <label htmlFor="er">{editNeedsReason ? 'Değişiklik gerekçesi *' : 'Değişiklik gerekçesi'}</label>
                    <input
                      id="er" className="inp"
                      value={editReason} onChange={(ev) => setEditReason(ev.target.value)}
                      placeholder="Puan geçmişinde görünür…"
                    />
                  </div>
                  {error ? <Note error>{error}</Note> : null}
                  <button className="btn good full" type="button"
                    disabled={busy || !canSaveEdit} onClick={submitEdit}>
                    <Icon name="check" /><span>{busy ? 'Kaydediliyor…' : 'Değişikliği kaydet'}</span>
                  </button>
                  <button className="btn ghost full" type="button" disabled={busy} onClick={cancelEdit}>
                    Vazgeç
                  </button>
                  {!live.complete ? (
                    <span className="faint" style={{ fontSize: 11.5, textAlign: 'center' }}>
                      Kaydetmek için beş kriterin tamamı puanlanmalı.
                    </span>
                  ) : null}
                  {editNeedsNote && !editNotes.trim() ? (
                    <span className="faint" style={{ fontSize: 11.5, textAlign: 'center' }}>
                      Bu puanda QMS notu zorunlu.
                    </span>
                  ) : null}
                  {editNeedsAudio && editAudioCount === 0 ? (
                    <span className="faint" style={{ fontSize: 11.5, textAlign: 'center' }}>
                      Çağrı kaydı zorunlu.
                    </span>
                  ) : null}
                  {editNeedsReason && !editReason.trim() ? (
                    <span className="faint" style={{ fontSize: 11.5, textAlign: 'center' }}>
                      Değişiklik gerekçesi zorunlu.
                    </span>
                  ) : null}
                  <Callout center>
                    Önceki puan kalıcı olarak saklanır ve puan geçmişinde görünür.
                  </Callout>
                </div>
              ) : null}

              {/* A plain correction. Hidden while an objection is open — that is answered
                  through the resolve form, which owes the agent a reply; the server refuses
                  an edit there (409 objection_open). */}
              {(e.canEdit || e.canDelete) && !canResolve && !editing && !revising ? (
                <div className="panel-foot">
                  {e.canEdit ? (
                    <button className="btn pri full" type="button" onClick={startEdit}>
                      <Icon name="edit_note" /><span>Değerlendirmeyi düzenle</span>
                    </button>
                  ) : null}
                  {/* Deletion is admin's always, and a QMS specialist's for 12h after
                      publication (server: canDeleteEvaluation). Two clicks: the first arms,
                      the second sends — the row, its puanlar and its recording all go. */}
                  {e.canDelete ? (
                    confirmDelete ? (
                      <>
                        <button className="btn danger full" type="button" disabled={busy} onClick={submitDelete}>
                          <Icon name="delete" /><span>{busy ? 'Siliniyor…' : 'Evet, kalıcı olarak sil'}</span>
                        </button>
                        <button className="btn ghost full" type="button" disabled={busy} onClick={() => setConfirmDelete(false)}>
                          Vazgeç
                        </button>
                        <Callout tone="bad" center>
                          Değerlendirme, puanları ve çağrı kayıtları kalıcı olarak silinir. Geri alınamaz.
                        </Callout>
                      </>
                    ) : (
                      <button className="btn ghost full" type="button" onClick={() => { setConfirmDelete(true); setError(''); }}>
                        <Icon name="delete" /><span>Değerlendirmeyi sil</span>
                      </button>
                    )
                  ) : null}
                  {error && !editing ? <Note error>{error}</Note> : null}
                </div>
              ) : null}

              {canResolve ? (
                <div className="panel-foot">
                  <div className="field">
                    <label htmlFor="rp">İtiraz yanıtı</label>
                    <textarea
                      id="rp" className="inp" style={{ minHeight: 88 }}
                      value={reply} onChange={(ev) => setReply(ev.target.value)}
                      placeholder="Agent'a iletilecek yanıt…"
                    />
                  </div>
                  {/* Same added-note field as the edit form: the reply lives in the objection
                      thread, this lands under the QMS note (migration 011). Optional. */}
                  <div className="field">
                    <label htmlFor="ran">Not ekle</label>
                    <textarea
                      id="ran" className="inp" style={{ minHeight: 72 }}
                      value={addNote} onChange={(ev) => setAddNote(ev.target.value)}
                      placeholder="Kendi adınızla eklenecek not…"
                    />
                    <span className="hint">QMS notunun altında, adınız ve saatiyle agent'a gösterilir.</span>
                  </div>
                  {error ? <Note error>{error}</Note> : null}
                  {revising ? (
                    <>
                      <button className="btn good full" type="button"
                        disabled={busy || !live.complete} onClick={() => submitResolution('revised')}>
                        <Icon name="check" /><span>{busy ? 'Kaydediliyor…' : 'Yeni puanı kaydet'}</span>
                      </button>
                      <button className="btn ghost full" type="button" disabled={busy} onClick={() => setRevising(false)}>
                        Vazgeç
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn pri full" type="button" onClick={startRevision}>
                        <Icon name="edit_note" /><span>Puanı güncelle</span>
                      </button>
                      <button className="btn danger full" type="button" disabled={busy}
                        onClick={() => submitResolution('upheld')}>
                        <Icon name="close" /><span>{busy ? '…' : 'İtirazı reddet (puan aynı kalsın)'}</span>
                      </button>
                    </>
                  )}
                </div>
              ) : null}
            </section>

            {!e.canObject && !canResolve && user.role === 'agent' && e.status !== 'published' ? (
              <Callout>
                Bu değerlendirme için itiraz süreci {e.status === 'objected' ? 'devam ediyor' : 'tamamlandı'}.
              </Callout>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export function EvalDetailFallback() {
  return <Empty>Değerlendirme bulunamadı.</Empty>;
}
